import { useState, useEffect, useCallback } from "react";
import { formatPlAmount, formatPlChangePercent } from "../utils/formatPlAmount";
import {
  getClientOverviewBootstrapData,
  getClientOverviewBootstrapError,
  getClientOverviewBootstrapPromise,
} from "../state/clientOverviewBootstrapStore";
import { useClientOverviewBootstrap } from "./useClientOverviewBootstrap";

export interface MonthAtGlanceData {
  monthLabel: string;
  booksClosed: boolean;
  noClosedMonth: boolean;
  asOfBooksClosedLabel: string | null;
  message: string | null;
  plSummary: {
    totalAmount: string;
    changePercent: string;
    changeLabel?: string;
    revenueAmount: string;
    expensesAmount: string;
    ebitdaAmount: string;
    /** Raw numeric values for dynamic bar heights (revenue, expense, ebitda) */
    revenueValue?: number;
    expenseValue?: number;
    ebitdaValue?: number;
  };
}

const DEFAULT_PL_SUMMARY = {
  totalAmount: "—",
  changePercent: "—",
  changeLabel: "Compare from last month" as const,
  revenueAmount: "—",
  expensesAmount: "—",
  ebitdaAmount: "—",
};

export function useMonthAtGlance(): MonthAtGlanceData & {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  useClientOverviewBootstrap();

  const [monthLabel, setMonthLabel] = useState("");
  const [booksClosed, setBooksClosed] = useState(false);
  const [noClosedMonth, setNoClosedMonth] = useState(false);
  const [asOfBooksClosedLabel, setAsOfBooksClosedLabel] = useState<
    string | null
  >(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [plSummary, setPlSummary] =
    useState<MonthAtGlanceData["plSummary"]>(DEFAULT_PL_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMonthAtGlance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let bootstrapEntity = getClientOverviewBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getClientOverviewBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getClientOverviewBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(bootstrapError ?? "Overview bootstrap data unavailable");
      }

      const section = bootstrapEntity.sections.monthAtGlance;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "Month at glance unavailable");
      }
      const entity = section.data;

      setMonthLabel(entity.monthLabel);
      setBooksClosed(entity.booksClosed);
      setNoClosedMonth(entity.noClosedMonth);
      setAsOfBooksClosedLabel(entity.asOfBooksClosedLabel);
      setInfoMessage(entity.message);
      setPlSummary({
        totalAmount: formatPlAmount(entity.plSummary.totalAmount),
        changePercent: formatPlChangePercent(entity.plSummary.changePercent),
        changeLabel: entity.plSummary.changeLabel,
        revenueAmount: formatPlAmount(entity.plSummary.revenue),
        expensesAmount: formatPlAmount(entity.plSummary.expense),
        ebitdaAmount: formatPlAmount(entity.plSummary.ebitda),
        revenueValue: entity.plSummary.revenue,
        expenseValue: entity.plSummary.expense,
        ebitdaValue: entity.plSummary.ebitda,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load month at glance";
      setError(message);
      setPlSummary(DEFAULT_PL_SUMMARY);
      setMonthLabel("");
      setBooksClosed(false);
      setNoClosedMonth(false);
      setAsOfBooksClosedLabel(null);
      setInfoMessage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonthAtGlance();
  }, [fetchMonthAtGlance]);

  return {
    monthLabel: monthLabel || "—",
    booksClosed,
    noClosedMonth,
    asOfBooksClosedLabel,
    message: infoMessage,
    plSummary,
    loading,
    error,
    refetch: fetchMonthAtGlance,
  };
}
